import React, { useState, useEffect } from "react";
import {
  collection,
  getDocs,
  updateDoc,
  doc,
  deleteDoc,
  query,
  where,
  addDoc,
  arrayUnion,
} from "firebase/firestore";
import { auth, db } from "../firebaseConfig";
import moment from "moment";
import "./ClockTime.css";

// Helper to build a consistent display name for users
const buildUserDisplayName = (u) =>
  (u.userName ||
    u.displayName ||
    [u.firstName || "", u.lastName || ""].filter(Boolean).join(" ") ||
    u.companyName || // allows entities like “Piccolo Construction LLC Worker 2”
    ""
  ).trim();

const normalizePersonName = (value = "") =>
  String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const resolveAssignedName = (assignment) => {
  if (typeof assignment === "string") return assignment;
  if (!assignment || typeof assignment !== "object") return "";
  return (
    assignment.name ||
    assignment.fullName ||
    assignment.displayName ||
    assignment.techName ||
    `${assignment.firstName || ""} ${assignment.lastName || ""}`.trim() ||
    assignment.email ||
    ""
  );
};

const toDateKey = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value.slice(0, 10);
  if (value?.toDate) return moment(value.toDate()).format("YYYY-MM-DD");
  return moment(value).isValid() ? moment(value).format("YYYY-MM-DD") : "";
};

const SubSummary = ({
  workedDates,
  salary,
  payments,
}) => {
  const totalDue = (workedDates?.length || 0) * (Number(salary) || 0);
  const totalPaid = (payments || []).reduce((sum, p) => sum + Number(p.amountPaid || 0), 0);
  const balance = totalDue - totalPaid;

  return (
    <div style={{ marginTop: 8 }}>
      <div><strong>Worked Days:</strong> {workedDates.length}</div>
      <div><strong>Daily Rate:</strong> ${Number(salary || 0).toFixed(2)}</div>
      <div><strong>Total Owed (days × rate):</strong> ${totalDue.toFixed(2)}</div>
      <div><strong>Total Paid:</strong> ${totalPaid.toFixed(2)}</div>
      <div><strong>Balance:</strong> ${balance.toFixed(2)}</div>
    </div>
  );
};

const ClockTime = () => {
  const [allTimeLogs, setAllTimeLogs] = useState([]);
  const [editingLog, setEditingLog] = useState(null);
  const [groupedLogs, setGroupedLogs] = useState({});
  const [selectedUserLogs, setSelectedUserLogs] = useState(null);
  const [showUserLogsPopup, setShowUserLogsPopup] = useState(false);
  const [isClockedIn, setIsClockedIn] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);

  const [showAddTimePopup, setShowAddTimePopup] = useState(false);
  const [showAddDayPopup, setShowAddDayPopup] = useState(false);

  const [usersList, setUsersList] = useState([]);
  const [missingAssignedDays, setMissingAssignedDays] = useState([]);
  const [checkingAssignedDays, setCheckingAssignedDays] = useState(false);
  const [addingAssignedDate, setAddingAssignedDate] = useState("");


  // NEW: also keep the selected user object when popup opens
  const [selectedUser, setSelectedUser] = useState(null);

  // NEW: payments loaded from subcontractorPay for selected user
  const [subPayments, setSubPayments] = useState([]);

  // NEW: payment entry form
  const [paymentDate, setPaymentDate] = useState("");
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentNote, setPaymentNote] = useState("");
  const [showWorkedDays, setShowWorkedDays] = useState(true);
  const [showPayments, setShowPayments] = useState(true);
  const [payingWorkDate, setPayingWorkDate] = useState("");

  // NEW: use userId for selection; keep userName for display only
  const [newTimeLog, setNewTimeLog] = useState({
    userId: "",
    clockIn: "",
    clockOut: "",
    duration: "",
    reason: "",
  });

  const [newDayLog, setNewDayLog] = useState({
    userId: "",
    days: [""],
    reason: "",
  });

  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  // Subcontractor persisted flag & daily rate
  const [isSub, setIsSub] = useState(false); // reflects users.isSubcontractor
  const [subDayRate, setSubDayRate] = useState(""); // separate Clock Time daily rate
  const [savingSubDayRate, setSavingSubDayRate] = useState(false);

  // Admin Access Control
  // Admin Access Control
useEffect(() => {
  const checkAccess = async () => {
    const user = auth.currentUser;

    if (!user) {
      alert("Access denied. Admins and Assistants only.");
      setAccessDenied(true);
      return;
    }

    try {
      const qy = query(collection(db, "users"), where("authUid", "==", user.uid));
      const userDoc = await getDocs(qy);

      if (userDoc.empty) {
        alert("Access denied. Admins and Assistants only.");
        setAccessDenied(true);
        return;
      }

      const userData = userDoc.docs[0]?.data();

      if (userData?.role !== "Admin" && userData?.role !== "Assistant") {
        alert("Access denied. Admins and Assistants only.");
        setAccessDenied(true);
        return;
      }

      // ✅ Access granted: do nothing, component continues normally
    } catch (error) {
      alert("An error occurred. Please try again.");
      setAccessDenied(true);
      return;
    }
  };

  checkAccess();
}, []);


  // Fetch all time logs
  const fetchAllTimeLogs = async () => {
    try {
      const logsRef = collection(db, "time_logs");
      const querySnapshot = await getDocs(logsRef);
      const logs = querySnapshot.docs.map((d) => {
        const data = d.data();
        return {
          id: d.id,
          ...data,
          clockIn: data.clockIn ? new Date(data.clockIn.toDate()) : null,
          clockOut: data.clockOut ? new Date(data.clockOut.toDate()) : null,
          payments: Array.isArray(data.payments) ? data.payments : [],
        };
      });

      setAllTimeLogs(logs);
      setGroupedLogs(
        logs.reduce((acc, log) => {
          const key = (log.userName || "Unknown").trim();
          if (!acc[key]) acc[key] = [];
          acc[key].push(log);
          return acc;
        }, {})
      );
    } catch (error) {
      console.error("Error fetching time logs:", error);
    }
  };

  // Fetch users
  const fetchUsers = async () => {
    try {
      const usersRef = collection(db, "users");
      const querySnapshot = await getDocs(usersRef);
      const users = querySnapshot.docs.map((d) => {
        const data = d.data();
        return {
          id: d.id,
          ...data,
          __display: buildUserDisplayName(data),
        };
      });
      users.sort((a, b) => a.__display.localeCompare(b.__display));
      setUsersList(users);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  useEffect(() => {
    fetchAllTimeLogs();
    fetchUsers();
  }, []);

  // Payments fetch for a user
  const fetchSubPayments = async (userId) => {
    try {
      const qy = query(collection(db, "subcontractorPay"), where("userId", "==", userId));
      const qs = await getDocs(qy);
      const items = qs.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => {
          const da = a.datePaid?.toDate ? a.datePaid.toDate() : new Date(a.datePaid);
          const dbb = b.datePaid?.toDate ? b.datePaid.toDate() : new Date(b.datePaid);
          return dbb - da;
        });
      setSubPayments(items);
    } catch (err) {
      console.error("Error fetching subcontractorPay:", err);
      setSubPayments([]);
    }
  };

    // helper: only logs that represent actual payable work
    const filterPayableLogs = (logs = []) =>
      logs.filter((l) => {
        const reason = (l.reason || "").toLowerCase();
  
        // ignore OFF days
        if (reason.includes("off")) return false;
  
        // ignore PAYMENT marker logs
        if (reason === "payment" || reason.includes("payment")) return false;
  
        // ignore zero-duration / marker logs
        if (!l.duration || l.duration <= 0) return false;
  
        // must have a clockIn to be counted
        return !!l.clockIn;
      });
  

  // worked date list (distinct days with work, excluding OFF notes)
 // worked date list (distinct days with work, excluding OFF notes and PAYMENT markers)
  // worked date list (distinct days with work, excluding OFF notes and PAYMENT markers)
  const getWorkedDates = (logs = []) => {
    const dates = new Set(
      filterPayableLogs(logs).map((l) =>
        moment(l.clockIn).format("YYYY-MM-DD")
      )
    );

    // sort newest first
    return Array.from(dates).sort((a, b) => (a < b ? 1 : -1));
  };



  // editing save
  const handleEditLog = async () => {
    try {
      const isOff = /off/i.test(editingLog.reason || "");

      const clockIn = editingLog.clockIn ? new Date(editingLog.clockIn) : null;
      const clockOut = isOff ? null : editingLog.clockOut ? new Date(editingLog.clockOut) : null;
      
      const duration = isOff
        ? 0
        : clockOut && clockIn
        ? Math.round((clockOut - clockIn) / 60000)
        : null;
      
      const logRef = doc(db, "time_logs", editingLog.id);
      await updateDoc(logRef, {
        clockIn,
        clockOut,
        duration,
        reason: editingLog.reason || "",
      });

      setSelectedUserLogs((prev) =>
        prev.map((log) =>
          log.id === editingLog.id ? { ...log, clockIn, clockOut, duration, reason: editingLog.reason } : log
        )
      );

      setEditingLog(null);
      fetchAllTimeLogs();
    } catch (error) {
      console.error("Error updating log:", error);
    }
  };

  // delete log
  const handleDeleteLog = async (id) => {
    try {
      const logRef = doc(db, "time_logs", id);
      await deleteDoc(logRef);

      const updatedLogs = allTimeLogs.filter((log) => log.id !== id);
      const updatedGrouped = updatedLogs.reduce((acc, log) => {
        const key = (log.userName || "Unknown").trim();
        if (!acc[key]) acc[key] = [];
        acc[key].push(log);
        return acc;
      }, {});

      setAllTimeLogs(updatedLogs);
      setGroupedLogs(updatedGrouped);

      if (selectedUserLogs) {
        const filteredUserLogs = selectedUserLogs.filter((log) => log.id !== id);
        setSelectedUserLogs(filteredUserLogs);
        if (filteredUserLogs.length === 0) {
          setShowUserLogsPopup(false);
          setSelectedUserLogs(null);
        }
      }
    } catch (error) {
      console.error("Error deleting log:", error);
    }
  };

  const userMatchesAssignment = (user, assignment) => {
    const assignedName = normalizePersonName(resolveAssignedName(assignment));
    if (!assignedName || !user) return false;

    const candidates = [
      buildUserDisplayName(user),
      user.name,
      user.fullName,
      user.displayName,
      user.employeeName,
      user.techName,
      `${user.firstName || ""} ${user.lastName || ""}`.trim(),
      user.email,
      user.companyName,
    ]
      .map(normalizePersonName)
      .filter(Boolean);

    return candidates.includes(assignedName);
  };

  const expandJobDates = (job = {}) => {
    const startKey = toDateKey(job.startDate || job.occurrenceDate);
    if (!startKey) return [];

    const rawEndKey = toDateKey(job.endDate || job.endDtate) || startKey;
    const endKey = rawEndKey < startKey ? startKey : rawEndKey;
    const today = moment().format("YYYY-MM-DD");
    const finalKey = endKey > today ? today : endKey;
    if (startKey > today || finalKey < startKey) return [];

    const dates = [];
    const cursor = moment(startKey, "YYYY-MM-DD");
    const finalDate = moment(finalKey, "YYYY-MM-DD");
    while (cursor.isSameOrBefore(finalDate, "day")) {
      dates.push(cursor.format("YYYY-MM-DD"));
      cursor.add(1, "day");
    }
    return dates;
  };

  const fetchMissingAssignedDays = async (user, currentLogs = []) => {
    if (!user) {
      setMissingAssignedDays([]);
      return;
    }

    const dismissedDateKeys = new Set(
      Array.isArray(user.dismissedTechAssignVerificationDates)
        ? user.dismissedTechAssignVerificationDates
        : []
    );

    setCheckingAssignedDays(true);
    try {
      const [jobsSnap, recurringSnap] = await Promise.all([
        getDocs(collection(db, "jobs")),
        getDocs(collection(db, "recurring_job_occurrences")),
      ]);

      const jobs = [
        ...jobsSnap.docs.map((d) => ({ id: d.id, ...d.data() })),
        ...recurringSnap.docs.map((d) => ({ id: d.id, ...d.data(), isRecurringOccurrence: true })),
      ];

      const existingDateKeys = new Set(
        (currentLogs || [])
          .filter((log) => {
            const reason = String(log.reason || "").toLowerCase();
            return log.clockIn && !reason.includes("payment");
          })
          .map((log) => moment(log.clockIn).format("YYYY-MM-DD"))
      );

      const missingByDate = new Map();

      jobs.forEach((job) => {
        const assignments = Array.isArray(job.techAssign)
          ? job.techAssign
          : job.techAssign
          ? [job.techAssign]
          : [];

        if (!assignments.some((assignment) => userMatchesAssignment(user, assignment))) return;

        expandJobDates(job).forEach((dateKey) => {
          if (existingDateKeys.has(dateKey) || dismissedDateKeys.has(dateKey)) return;

          const existing = missingByDate.get(dateKey) || {
            dateKey,
            jobs: [],
          };

          existing.jobs.push({
            jobId: job.id,
            wo: job.wo || job.WO || "",
            siteName: job.siteName || job.propertyName || "",
          });
          missingByDate.set(dateKey, existing);
        });
      });

      setMissingAssignedDays(
        Array.from(missingByDate.values()).sort((a, b) =>
          b.dateKey.localeCompare(a.dateKey)
        )
      );
    } catch (error) {
      console.error("Error checking techAssign dates:", error);
      alert("Unable to check assigned job dates. Please try again.");
    } finally {
      setCheckingAssignedDays(false);
    }
  };

  const handleDismissAssignedDay = async (missingDay) => {
    if (!selectedUser || !missingDay?.dateKey) return;

    try {
      const userRef = doc(db, "users", selectedUser.id);
      await updateDoc(userRef, {
        dismissedTechAssignVerificationDates: arrayUnion(missingDay.dateKey),
      });

      setMissingAssignedDays((prev) =>
        prev.filter((item) => item.dateKey !== missingDay.dateKey)
      );

      setSelectedUser((prev) =>
        prev
          ? {
              ...prev,
              dismissedTechAssignVerificationDates: Array.from(
                new Set([
                  ...(prev.dismissedTechAssignVerificationDates || []),
                  missingDay.dateKey,
                ])
              ),
            }
          : prev
      );

      setUsersList((prev) =>
        prev.map((user) =>
          user.id === selectedUser.id
            ? {
                ...user,
                dismissedTechAssignVerificationDates: Array.from(
                  new Set([
                    ...(user.dismissedTechAssignVerificationDates || []),
                    missingDay.dateKey,
                  ])
                ),
              }
            : user
        )
      );
    } catch (error) {
      console.error("Error dismissing assigned-day verification:", error);
      alert("The verification could not be dismissed.");
    }
  };

  const handleAddVerifiedAssignedDay = async (missingDay) => {
    if (!selectedUser || !missingDay?.dateKey) return;

    setAddingAssignedDate(missingDay.dateKey);
    try {
      const freshLogsSnap = await getDocs(collection(db, "time_logs"));
      const selectedIds = new Set(
        [selectedUser.id, selectedUser.authUid].filter(Boolean).map(String)
      );
      const selectedName = normalizePersonName(buildUserDisplayName(selectedUser));

      const alreadyExists = freshLogsSnap.docs.some((d) => {
        const data = d.data();
        const reason = String(data.reason || "").toLowerCase();
        const belongsToUser =
          selectedIds.has(String(data.userId || "")) ||
          normalizePersonName(data.userName || "") === selectedName;

        return (
          belongsToUser &&
          data.clockIn &&
          !reason.includes("payment") &&
          toDateKey(data.clockIn) === missingDay.dateKey
        );
      });

      if (alreadyExists) {
        alert("A Clock Time entry already exists for this user on that date.");
        setMissingAssignedDays((prev) =>
          prev.filter((item) => item.dateKey !== missingDay.dateKey)
        );
        await fetchAllTimeLogs();
        return;
      }

      const clockIn = new Date(`${missingDay.dateKey}T08:00:00`);
      const clockOut = new Date(`${missingDay.dateKey}T16:00:00`);
      const jobIds = missingDay.jobs.map((job) => job.jobId).filter(Boolean);
      const workOrders = missingDay.jobs.map((job) => job.wo).filter(Boolean);

      const newLogData = {
        userId: selectedUser.authUid || selectedUser.id,
        userName: buildUserDisplayName(selectedUser),
        clockIn,
        clockOut,
        duration: 480,
        reason: "Verified from techAssign",
        verifiedFromTechAssign: true,
        verifiedAt: new Date(),
        jobIds,
        jobId: jobIds[0] || null,
        workOrders,
        payments: [],
      };

      const created = await addDoc(collection(db, "time_logs"), newLogData);
      const newLog = { id: created.id, ...newLogData };

      setAllTimeLogs((prev) => [...prev, newLog]);
      setSelectedUserLogs((prev) =>
        [...(prev || []), newLog].sort((a, b) => b.clockIn - a.clockIn)
      );
      setGroupedLogs((prev) => {
        const display = buildUserDisplayName(selectedUser);
        return {
          ...prev,
          [display]: [...(prev[display] || []), newLog],
        };
      });
      setMissingAssignedDays((prev) =>
        prev.filter((item) => item.dateKey !== missingDay.dateKey)
      );
    } catch (error) {
      console.error("Error adding verified assigned day:", error);
      alert("The verified day could not be added.");
    } finally {
      setAddingAssignedDate("");
    }
  };

  // open user logs
  const handleShowUserLogs = async (userName) => {
    const logs = (groupedLogs[userName] || []).sort((a, b) => b.clockIn - a.clockIn);
    setSelectedUserLogs(logs);
    setShowUserLogsPopup(true);
    const activeLog = logs.find((log) => !log.clockOut);
    setIsClockedIn(!!activeLog);

    // find user
    const user = usersList.find((u) => buildUserDisplayName(u) === userName);
    setSelectedUser(user || null);

    // set isSub from user & default day rate from salary
    const sub = !!user?.isSubcontractor;
    setIsSub(sub);
    const hourlyRate = Number(user?.salary || user?.hourlyRate || user?.payRate || 0);
    const defaultDailyRate = hourlyRate > 0 ? hourlyRate * 8 : 0;
    setSubDayRate(
      user?.clockTimeDailyRate != null && user?.clockTimeDailyRate !== ""
        ? String(user.clockTimeDailyRate)
        : defaultDailyRate > 0
        ? String(defaultDailyRate)
        : ""
    );

    // get payments
    if (user?.id) fetchSubPayments(user.id);

    // reset payment form
    setPaymentDate("");
    setPaymentAmount("");
    setPaymentNote("");

    await fetchMissingAssignedDays(user || null, logs);
  };

  const handleCloseUserLogsPopup = () => {
    setShowUserLogsPopup(false);
    setSelectedUserLogs(null);
    setSelectedUser(null);
    setSubPayments([]);
    setMissingAssignedDays([]);
  };


  const handleSaveSubDayRate = async () => {
    if (!selectedUser?.id) return;

    const dailyRate = Number(subDayRate);
    if (!Number.isFinite(dailyRate) || dailyRate <= 0) {
      alert("Enter a valid daily rate greater than $0.00.");
      return;
    }

    setSavingSubDayRate(true);
    try {
      // Save to a Clock Time-specific field. Do not change Staff.salary,
      // which remains the employee/subcontractor hourly rate.
      await updateDoc(doc(db, "users", selectedUser.id), {
        clockTimeDailyRate: dailyRate,
        clockTimeDailyRateUpdatedAt: new Date(),
      });

      setSelectedUser((prev) =>
        prev ? { ...prev, clockTimeDailyRate: dailyRate } : prev
      );
      setUsersList((prev) =>
        prev.map((user) =>
          user.id === selectedUser.id
            ? { ...user, clockTimeDailyRate: dailyRate }
            : user
        )
      );

      alert(`Daily rate saved at $${dailyRate.toFixed(2)}. Staff hourly rate was not changed.`);
    } catch (error) {
      console.error("Failed to save Clock Time daily rate:", error);
      alert("The daily rate could not be saved.");
    } finally {
      setSavingSubDayRate(false);
    }
  };

  // toggle sub flag & persist on user
  const handleToggleSub = async (checked) => {
    setIsSub(checked);
    if (!selectedUser) return;
    try {
      const userRef = doc(db, "users", selectedUser.id);
      await updateDoc(userRef, { isSubcontractor: checked });
      // refresh local usersList
      setUsersList((prev) =>
        prev.map((u) => (u.id === selectedUser.id ? { ...u, isSubcontractor: checked } : u))
      );
    } catch (err) {
      console.error("Failed to update isSubcontractor:", err);
    }
  };

  // clock toggle
  const handleClockToggle = async (userName) => {
    const userLogs = groupedLogs[userName] || [];
    const activeLog = userLogs.find((log) => !log.clockOut);

    const user = usersList.find((u) => buildUserDisplayName(u) === userName);
    if (!user) {
      alert("User not found in users list. Please check the user record.");
      return;
    }
    const userId = user.id;

    if (!activeLog) {
      try {
        await addDoc(collection(db, "time_logs"), {
          userId,
          userName,
          jobId: null, // will be assigned later
          clockIn: new Date(),
          clockOut: null,
          duration: null,
          payments: [],
        });
        setIsClockedIn(true);
        fetchAllTimeLogs();
      } catch (error) {
        console.error("Error clocking in:", error);
      }
    } else {
      try {
        const clockOutTime = new Date();
        const logRef = doc(db, "time_logs", activeLog.id);
        const duration = Math.round((clockOutTime - activeLog.clockIn) / 60000);
        await updateDoc(logRef, {
          clockOut: clockOutTime,
          duration,
        });
        setIsClockedIn(false);
        fetchAllTimeLogs();
      } catch (error) {
        console.error("Error clocking out:", error);
      }
    }
  };

  // compute hours
   // compute total payable hours (ignores OFF / PAYMENT / zero-duration)
   const calculateTotalHours = (logs = []) => {
    const payableLogs = filterPayableLogs(logs);
    const totalMinutes = payableLogs.reduce(
      (total, log) => (log.duration ? total + log.duration : total),
      0
    );
    // round to nearest 15 minutes, then convert to hours
    return (Math.round(totalMinutes / 15) * 15) / 60;
  };

  const calculateHalfMonthStats = (logs = []) => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth(); // 0-based
    const day = now.getDate();

    // First half: always 1st–14th of the CURRENT month
    const firstStart = new Date(year, month, 1, 0, 0, 0, 0);
    const firstEnd = new Date(year, month, 14, 23, 59, 59, 999);

    // Second half:
    // - If today is 1–14, we look at the PREVIOUS month 15–end
    // - If today is 15–end, we look at the CURRENT month 15–end
    let secondStart, secondEnd;
    if (day <= 14) {
      const prevMonthDate = new Date(year, month - 1, 1);
      const prevYear = prevMonthDate.getFullYear();
      const prevMonth = prevMonthDate.getMonth();
      secondStart = new Date(prevYear, prevMonth, 15, 0, 0, 0, 0);
      secondEnd = new Date(prevYear, prevMonth + 1, 0, 23, 59, 59, 999); // last day prev month
    } else {
      secondStart = new Date(year, month, 15, 0, 0, 0, 0);
      secondEnd = new Date(year, month + 1, 0, 23, 59, 59, 999); // last day this month
    }

    // Filter logs into those windows
    const firstHalfLogs = logs.filter(
      (log) => log.clockIn && log.clockIn >= firstStart && log.clockIn <= firstEnd
    );
    const secondHalfLogs = logs.filter(
      (log) => log.clockIn && log.clockIn >= secondStart && log.clockIn <= secondEnd
    );

    // Apply the same payable filter for days
    const firstHalfPayable = filterPayableLogs(firstHalfLogs);
    const secondHalfPayable = filterPayableLogs(secondHalfLogs);

    const firstHalfDays = new Set(
      firstHalfPayable.map((log) => moment(log.clockIn).format("YYYY-MM-DD"))
    ).size;
    const secondHalfDays = new Set(
      secondHalfPayable.map((log) => moment(log.clockIn).format("YYYY-MM-DD"))
    ).size;

    // Raw payable hours (no lunch deduction)
    const firstHalfHours = calculateTotalHours(firstHalfLogs);
    const secondHalfHours = calculateTotalHours(secondHalfLogs);

    // New: hours minus 1h lunch per worked day
    const firstHalfLunchHours = Math.max(0, firstHalfHours - firstHalfDays * 1);
    const secondHalfLunchHours = Math.max(0, secondHalfHours - secondHalfDays * 1);

    // Pay day labels:
    //  - 1st–14th → 25th of the CURRENT month
    //  - 15th–end → 10th of the FOLLOWING month
    const firstHalfPayDate = new Date(year, month, 25);
    const secondHalfPayDate =
      day <= 14
        ? new Date(year, month, 10) // paying previous month 15–end
        : new Date(year, month + 1, 10); // paying current 15–end next month

    // Which period is "current" vs "last"?
    const firstIsCurrent = day <= 14;
    const secondIsCurrent = !firstIsCurrent;

    return {
      firstHalfDays,
      firstHalfHours,
      firstHalfLunchHours,   // <- NEW
      secondHalfDays,
      secondHalfHours,
      secondHalfLunchHours,  // <- NEW

      firstHalfPayMonth: moment(firstHalfPayDate).format("MMMM"),
      secondHalfPayMonth: moment(secondHalfPayDate).format("MMMM"),
      firstIsCurrent,
      secondIsCurrent,

      firstStart,
      firstEnd,
      secondStart,
      secondEnd,
    };
  };

  // Detect an active log (no clockOut) running longer than 10 hours
  const getLongActiveLogInfo = (logs = []) => {
    const now = new Date();
    const active = (logs || []).find(
      (l) =>
        l.clockIn &&
        !l.clockOut &&
        !/off/i.test(l.reason || "")
    );
    
    if (!active) return null;
    const minutes = Math.round((now - active.clockIn) / 60000);
    if (minutes < 600) return null; // under 10h, ignore
    return { log: active, minutes };
  };

  const formatDuration = (minutes) => {
    const m = Number(minutes || 0);
    const hours = Math.floor(m / 60);
    const remainingMinutes = m % 60;
    return `${hours}h ${remainingMinutes}m`;
  };

  // Controlled inputs
  const handleAddTimeInputChange = (e) => {
    const { name, value } = e.target;
    setNewTimeLog((prev) => {
      const updated = { ...prev, [name]: value };
      if (name === "clockIn" || name === "clockOut") {
        const clockIn = updated.clockIn ? new Date(updated.clockIn) : null;
        const clockOut = updated.clockOut ? new Date(updated.clockOut) : null;
        if (clockIn && clockOut && clockOut > clockIn) {
          updated.duration = Math.round((clockOut - clockIn) / 60000);
        } else {
          updated.duration = "";
        }
      }
      return updated;
    });
  };

  const handleSaveNewTimeLog = async () => {
    try {
      const user = usersList.find((u) => u.id === newTimeLog.userId);
      if (user) {
        const userId = user.id;
        const display = user.__display;
        const newLog = {
          userId,
          userName: display,
          clockIn: new Date(newTimeLog.clockIn),
          clockOut: new Date(newTimeLog.clockOut),
          duration: newTimeLog.duration,
          payments: [],
        };
        if ((newTimeLog.reason || "").trim() !== "") {
          newLog.reason = newTimeLog.reason;
        }
        await addDoc(collection(db, "time_logs"), newLog);
        setShowAddTimePopup(false);
        setNewTimeLog({ userId: "", clockIn: "", clockOut: "", duration: "", reason: "" });
        fetchAllTimeLogs();
      } else {
        alert("User not found. Please ensure the name matches a user in the system.");
      }
    } catch (error) {
      console.error("Error adding new time log:", error);
    }
  };

  const handleCancelNewTimeLog = () => {
    setShowAddTimePopup(false);
    setNewTimeLog({ userId: "", clockIn: "", clockOut: "", duration: "", reason: "" });
  };

  const handleEditLogClick = (log) => {
    setEditingLog({
      ...log,
      clockIn: log.clockIn ? new Date(log.clockIn) : null,
      clockOut: log.clockOut ? new Date(log.clockOut) : null,
    });
  };
  
  const formatDateForInput = (date) => {
    if (!date) return "";
  
    // make sure we have a valid Date object
    const d = date instanceof Date ? date : new Date(date);
    if (isNaN(d.getTime())) {
      // invalid date – don't try to format
      return "";
    }
  
    const offsetDate = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
    return offsetDate.toISOString().slice(0, -8);
  };
  

  const handleAddDayInputChange = (e, index) => {
    const { name, value } = e.target;
    if (name === "userId" || name === "reason") {
      setNewDayLog((prev) => ({ ...prev, [name]: value }));
    } else if (name === "day") {
      const updatedDays = [...newDayLog.days];
      updatedDays[index] = value;
      setNewDayLog((prev) => ({ ...prev, days: updatedDays }));
    }
  };

  const handleAddAnotherDay = () => {
    setNewDayLog((prev) => ({ ...prev, days: [...prev.days, ""] }));
  };

  const handleSaveNewDayLog = async () => {
    try {
      const user = usersList.find((u) => u.id === newDayLog.userId);
      if (user) {
        const userId = user.id;
        const display = user.__display;
        const isOff = /off/i.test(newDayLog.reason || "");

        const dayLogs = newDayLog.days.map((day) => {
          // Explicitly build a CLOSED day shift
          const clockIn = isOff
          ? new Date(`${day}T12:00:00`)
          : new Date(`${day}T08:00:00`);
        
        const clockOut = isOff
          ? new Date(`${day}T12:00:00`) // 👈 CLOSE IT IMMEDIATELY
          : new Date(`${day}T16:00:00`);        
        
          return {
            userId,
            userName: display,
            clockIn,
            clockOut,
            duration: isOff ? 0 : 480, // force exactly 8 hours
            reason: newDayLog.reason || "",
            payments: [],
          };
        });
        
        const addDayPromises = dayLogs.map((log) => addDoc(collection(db, "time_logs"), log));
        await Promise.all(addDayPromises);

        setShowAddDayPopup(false);
        setNewDayLog({ userId: "", days: [""], reason: "" });
        fetchAllTimeLogs();
      } else {
        alert("User not found. Please ensure the name matches a user in the system.");
      }
    } catch (error) {
      console.error("Error adding new day log:", error);
    }
  };

  const handleCancelNewDayLog = () => {
    setShowAddDayPopup(false);
    setNewDayLog({ userId: "", days: [""], reason: "" });
  };

  const saveCanonicalSubcontractorPayment = async (paymentInput = {}) => {
    const amount = Number(paymentInput.amount ?? paymentInput.amountPaid ?? 0);
    const paymentDateValue = paymentInput.date || paymentInput.datePaid || new Date();
    const paymentDateKey = toDateKey(paymentDateValue) || moment().format("YYYY-MM-DD");
    const subcontractorName =
      paymentInput.subcontractorName ||
      paymentInput.userName ||
      (selectedUser ? buildUserDisplayName(selectedUser) : "");

    const payment = {
      ...paymentInput,
      userId: paymentInput.userId || selectedUser?.id || "",
      subcontractorId:
        paymentInput.subcontractorId || paymentInput.userId || selectedUser?.id || "",
      userName: subcontractorName,
      subcontractorName,
      vendor: subcontractorName,
      property: paymentInput.property || subcontractorName,
      amount,
      amountPaid: amount,
      date: paymentDateKey,
      datePaid: paymentInput.datePaid || paymentDateValue,
      category: paymentInput.category || "Subcontractor",
      effect: "subtract",
      sourcePage: "ClockTime",
      createdAt: paymentInput.createdAt || new Date(),
    };

    const paymentRef = await addDoc(collection(db, "subcontractorPay"), payment);
    const transaction = {
      date: paymentDateKey,
      type: "expense",
      amount,
      category: "Subcontractor",
      vendor: subcontractorName,
      memo: payment.note || paymentInput.note || "Clock Time subcontractor payment",
      jobId: paymentInput.jobId || null,
      method: paymentInput.method || "manual",
      sourceCollection: "subcontractorPay",
      sourceId: paymentRef.id,
      workDate: paymentInput.workDate || "",
      updatedAt: new Date(),
    };
    const txRef = await addDoc(collection(db, "transactions"), transaction);
    await updateDoc(doc(db, "subcontractorPay", paymentRef.id), { txId: txRef.id });
    return { id: paymentRef.id, ...payment, txId: txRef.id };
  };

  const getPaymentsForWorkDate = (workDate) =>
    (subPayments || []).filter((payment) => payment.workDate === workDate);

  const getPaidForWorkDate = (workDate) =>
    getPaymentsForWorkDate(workDate).reduce(
      (sum, payment) => sum + Number(payment.amountPaid || 0),
      0
    );

  const handlePayWorkedDay = async (workDate) => {
    if (!selectedUser || !workDate) return;

    const dailyRate = Number(subDayRate || 0);
    if (dailyRate <= 0) {
      alert("Enter a valid Daily Rate before recording payment.");
      return;
    }

    const alreadyPaid = getPaidForWorkDate(workDate);
    const remaining = Math.max(0, dailyRate - alreadyPaid);
    if (remaining <= 0.005) {
      alert("This work day is already paid in full.");
      return;
    }

    const entered = window.prompt(
      `Amount to pay for ${moment(workDate).format("M/D/YYYY")}\n\nDaily rate: $${dailyRate.toFixed(2)}\nAlready paid: $${alreadyPaid.toFixed(2)}\nRemaining: $${remaining.toFixed(2)}\n\nEnter the payment amount. Leave the full remaining amount to mark it paid in full.`,
      remaining.toFixed(2)
    );

    if (entered === null) return;

    const amount = Number(String(entered).replace(/[$,]/g, "").trim());
    if (!Number.isFinite(amount) || amount <= 0) {
      alert("Enter a valid payment amount greater than $0.00.");
      return;
    }
    if (amount > remaining + 0.005) {
      alert(`The payment cannot be more than the remaining balance of $${remaining.toFixed(2)}.`);
      return;
    }

    setPayingWorkDate(workDate);
    try {
      const paymentDateObj = new Date();
      const newTotalPaid = alreadyPaid + amount;
      const paidInFull = newTotalPaid >= dailyRate - 0.005;
      const payment = {
        userId: selectedUser.id,
        userName: buildUserDisplayName(selectedUser),
        workDate,
        datePaid: paymentDateObj,
        amountPaid: amount,
        dailyRateAtPayment: dailyRate,
        paymentType: paidInFull ? "full" : "partial",
        note: paidInFull
          ? `Paid in full for work day ${workDate}`
          : `Partial payment for work day ${workDate}`,
        createdAt: new Date(),
      };

      await saveCanonicalSubcontractorPayment(payment);

      const matchingLogs = (selectedUserLogs || []).filter(
        (log) => log.clockIn && moment(log.clockIn).format("YYYY-MM-DD") === workDate
      );

      await Promise.all(
        matchingLogs.map(async (log) => {
          const logRef = doc(db, "time_logs", log.id);
          const nextPayments = Array.isArray(log.payments) ? [...log.payments] : [];
          nextPayments.push({
            workDate,
            datePaid: paymentDateObj,
            amountPaid: amount,
            dailyRateAtPayment: dailyRate,
            paymentType: paidInFull ? "full" : "partial",
            note: payment.note,
            createdAt: new Date(),
          });
          await updateDoc(logRef, { payments: nextPayments });
        })
      );

      await fetchSubPayments(selectedUser.id);
      await fetchAllTimeLogs();
      alert(
        paidInFull
          ? `Work day ${moment(workDate).format("M/D/YYYY")} is paid in full.`
          : `Partial payment recorded. $${Math.max(0, dailyRate - newTotalPaid).toFixed(2)} remains for this work day.`
      );
    } catch (error) {
      console.error("Failed to pay worked day:", error);
      alert("The payment could not be recorded. Please try again.");
    } finally {
      setPayingWorkDate("");
    }
  };

  // NEW: record subcontractor payment
  const handleRecordPayment = async () => {
    try {
      if (!selectedUser) {
        alert("No user selected.");
        return;
      }
      if (!isSub) {
        alert("Enable 'Treat as Subcontractor' first.");
        return;
      }
      if (!paymentDate || !paymentAmount) {
        alert("Please enter payment date and amount.");
        return;
      }

      const dateObj = new Date(`${paymentDate}T12:00:00`); // noon to avoid tz mismatch
      const payment = {
        userId: selectedUser.id,
        userName: buildUserDisplayName(selectedUser),
        datePaid: dateObj,
        amountPaid: Number(paymentAmount),
        note: paymentNote || "",
        createdAt: new Date(),
      };

      // 1) Write one canonical payment and mirror it to transactions
      await saveCanonicalSubcontractorPayment(payment);

      // 2) Also save a reference/note into past time logs on the paid date
      //    Find logs for selected user that match the payment date (by YYYY-MM-DD)
      const paidDay = moment(dateObj).format("YYYY-MM-DD");
      const matchingLogs = (selectedUserLogs || []).filter(
        (l) => moment(l.clockIn).format("YYYY-MM-DD") === paidDay
      );

      if (matchingLogs.length > 0) {
        // push payment into each matching log.payments
        await Promise.all(
          matchingLogs.map(async (log) => {
            const logRef = doc(db, "time_logs", log.id);
            const nextPayments = Array.isArray(log.payments) ? [...log.payments] : [];
            nextPayments.push({
              datePaid: dateObj,
              amountPaid: Number(paymentAmount),
              note: paymentNote || "",
              createdAt: new Date(),
            });
            await updateDoc(logRef, { payments: nextPayments });
          })
        );
      } else {
        // No log for that day: create a zero-duration PAYMENT marker log
        await addDoc(collection(db, "time_logs"), {
          userId: selectedUser.id,
          userName: buildUserDisplayName(selectedUser),
          clockIn: dateObj,
          clockOut: dateObj,
          duration: 0,
          reason: "PAYMENT",
          payments: [
            {
              datePaid: dateObj,
              amountPaid: Number(paymentAmount),
              note: paymentNote || "",
              createdAt: new Date(),
            },
          ],
        });
      }

      // refresh payments and logs
      await fetchSubPayments(selectedUser.id);
      await fetchAllTimeLogs();

      // refresh selectedUserLogs view to include payment markers/updates
      if (selectedUser) {
        const display = buildUserDisplayName(selectedUser);
        const logs = (groupedLogs[display] || []).sort((a, b) => b.clockIn - a.clockIn);
        setSelectedUserLogs(logs);
      }

      // clear form
      setPaymentDate("");
      setPaymentAmount("");
      setPaymentNote("");
      alert("Payment recorded.");
    } catch (err) {
      console.error("Failed to record payment:", err);
      alert("Failed to record payment. See console for details.");
    }
  };

  // Worked dates for selected user
  const workedDates = getWorkedDates(selectedUserLogs || []);
  if (accessDenied) {
    return (
      <div className="clock-time-page" style={{ padding: 16 }}>
        <h1>Clock Time</h1>
        <p style={{ color: "#b91c1c", fontWeight: 700 }}>
          Access denied. Admins and Assistants only.
        </p>
      </div>
    );
  }
  
  return (
    <div className="clock-time-page">
      <h1>All Users' Clock In/Out Logs</h1>

      <div className="modal-button">
        <button className="green-button" onClick={() => setShowAddTimePopup(true)}>Add Time</button>
        <button className="yellow-button" onClick={() => setShowAddDayPopup(true)}>Add Day</button>
      </div>

      <div className="user-containers">
      {Object.keys(groupedLogs).map((userName) => {
  const userLogs = groupedLogs[userName];
  const stats = calculateHalfMonthStats(userLogs);
  const longActive = getLongActiveLogInfo(userLogs);
  return (
    <div key={userName} className="user-container">
      <h2>{userName}</h2>
      <button onClick={() => handleShowUserLogs(userName)}>Past Time Logs</button>
      
      {/* Yellow warning box if they have an active log > 10h */}
      {longActive && (
        <div
          style={{
            marginTop: 6,
            marginBottom: 6,
            padding: 8,
            backgroundColor: "#fff8c4",
            border: "1px solid #f0d96a",
            borderRadius: 6,
            fontSize: "0.9rem",
          }}
        >
          ⚠️ <strong>Possible missing clock-out</strong><br />
          Active since: {longActive.log.clockIn.toLocaleString()}<br />
          Elapsed: {formatDuration(longActive.minutes)}
        </div>
      )}

      <div>
        {stats.firstIsCurrent ? "Current period" : "Last period"} — 1st–14th (
        {stats.firstHalfPayMonth} 25th Pay Day):<br />
        Days: {stats.firstHalfDays} &nbsp;|&nbsp; Hours (no lunch): {stats.firstHalfHours} &nbsp;|&nbsp;
        Hours (-1h/day lunch): {stats.firstHalfLunchHours}
      </div>
      <div style={{ marginTop: 4 }}>
        {stats.secondIsCurrent ? "Current period" : "Last period"} — 15th–end (
        {stats.secondHalfPayMonth} 10th Pay Day):<br />
        Days: {stats.secondHalfDays} &nbsp;|&nbsp; Hours (no lunch): {stats.secondHalfHours} &nbsp;|&nbsp;
        Hours (-1h/day lunch): {stats.secondHalfLunchHours}
      </div>
    </div>
  );
})}

      </div>

      {showUserLogsPopup && (
        <div className="user-logs-popup">
          <div className="user-logs-popup-content">
            <button className="close-button" onClick={handleCloseUserLogsPopup}>Close</button>

            {selectedUser ? (
              <>
                <h3>{buildUserDisplayName(selectedUser)}'s Logs</h3>
                <button onClick={() => handleClockToggle(buildUserDisplayName(selectedUser))}>
                  {isClockedIn ? "Clock Out" : "Clock In"}
                </button>

                {/* Subcontractor panel */}
                <div className="sub-panel" style={{ margin: "12px 0", padding: 10, border: "1px solid #ddd", borderRadius: 8 }}>
                  <label style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8 }}>
                    <input
                      type="checkbox"
                      checked={isSub}
                      onChange={(e) => handleToggleSub(e.target.checked)}
                    />
                    Treat as Subcontractor (persists on user)
                  </label>

                  {isSub && (
                    <>
                      <div style={{ display: "grid", gap: 10, gridTemplateColumns: "1fr 1fr" }}>
                        <div>
                          <label>Daily Rate (Clock Time only)</label>
                          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={subDayRate}
                              onChange={(e) => setSubDayRate(e.target.value)}
                              placeholder="Daily rate"
                              style={{ flex: "1 1 150px" }}
                            />
                            <button
                              type="button"
                              className="green-button"
                              disabled={savingSubDayRate}
                              onClick={handleSaveSubDayRate}
                            >
                              {savingSubDayRate ? "Saving..." : "Save Daily Rate"}
                            </button>
                          </div>
                          <small style={{ display: "block", opacity: 0.7 }}>
                            Defaults to the Staff hourly rate × 8 hours. For example, $40/hour becomes $320/day.
                            Saving here updates only <code>users.clockTimeDailyRate</code> and does not change the Staff hourly rate.
                          </small>
                        </div>
                        <div />
                      </div>

                      {/* Summary */}
                      <SubSummary
                        workedDates={workedDates}
                        salary={subDayRate || 0}
                        payments={subPayments}
                      />

                      {/* Worked days list with per-day payment tracking */}
                      <div className="sub-collapsible-section">
                        <div className="sub-section-heading">
                          <strong>Worked Days:</strong>
                          <button
                            type="button"
                            className="sub-hide-button"
                            onClick={() => setShowWorkedDays((prev) => !prev)}
                          >
                            {showWorkedDays ? "Hide Work Days" : "Show Work Days"}
                          </button>
                        </div>

                        {showWorkedDays && (
                          <ul className="worked-days-list">
                            {workedDates.map((d) => {
                              const dailyRate = Number(subDayRate || 0);
                              const paidForDay = getPaidForWorkDate(d);
                              const remainingForDay = Math.max(0, dailyRate - paidForDay);
                              const isPaidFull = dailyRate > 0 && remainingForDay <= 0.005;
                              const isPaidPartial = paidForDay > 0 && !isPaidFull;

                              return (
                                <li key={d} className="worked-day-row">
                                  <div className="worked-day-details">
                                    <strong>{moment(d).format("M/D/YYYY")}</strong>
                                    <span>Daily Rate: ${dailyRate.toFixed(2)}</span>
                                    <span>Paid: ${paidForDay.toFixed(2)}</span>
                                    <span>Remaining: ${remainingForDay.toFixed(2)}</span>
                                    <span
                                      className={
                                        isPaidFull
                                          ? "payment-status paid-full"
                                          : isPaidPartial
                                          ? "payment-status paid-partial"
                                          : "payment-status unpaid"
                                      }
                                    >
                                      {isPaidFull
                                        ? "Paid Full"
                                        : isPaidPartial
                                        ? "Paid Partial"
                                        : "Unpaid"}
                                    </span>
                                  </div>
                                  <button
                                    type="button"
                                    className="worked-day-paid-button"
                                    disabled={isPaidFull || payingWorkDate === d}
                                    onClick={() => handlePayWorkedDay(d)}
                                  >
                                    {payingWorkDate === d
                                      ? "Saving..."
                                      : isPaidFull
                                      ? "Paid"
                                      : paidForDay > 0
                                      ? "Pay Remaining"
                                      : "Paid"}
                                  </button>
                                </li>
                              );
                            })}
                            {workedDates.length === 0 && <li>No worked days recorded.</li>}
                          </ul>
                        )}
                      </div>

                      {/* Payments list */}
                      <div className="sub-collapsible-section">
                        <div className="sub-section-heading">
                          <strong>Payments:</strong>
                          <button
                            type="button"
                            className="sub-hide-button"
                            onClick={() => setShowPayments((prev) => !prev)}
                          >
                            {showPayments ? "Hide Payments" : "Show Payments"}
                          </button>
                        </div>

                        {showPayments && (
                          <ul className="sub-payments-list">
                            {subPayments.length > 0 ? (
                              subPayments.map((p) => {
                                const dt = p.datePaid?.toDate ? p.datePaid.toDate() : new Date(p.datePaid);
                                return (
                                  <li key={p.id}>
                                    {moment(dt).format("M/D/YYYY")} — Paid ${Number(p.amountPaid || 0).toFixed(2)}
                                    {p.workDate
                                      ? ` — Work Day: ${moment(p.workDate).format("M/D/YYYY")}`
                                      : ""}
                                    {p.paymentType === "partial" ? " — Partial" : ""}
                                    {p.paymentType === "full" ? " — Paid Full" : ""}
                                    {p.note ? ` — Note: ${p.note}` : ""}
                                  </li>
                                );
                              })
                            ) : (
                              <li>No payments recorded.</li>
                            )}
                          </ul>
                        )}
                      </div>

                      {/* Record payment form */}
                      <div style={{ marginTop: 12, paddingTop: 10, borderTop: "1px dashed #ccc" }}>
                        <h4 style={{ margin: "6px 0" }}>Record Payment</h4>
                        <div style={{ display: "grid", gap: 10, gridTemplateColumns: "1fr 1fr 2fr auto" }}>
                          <div>
                            <label>Date Paid</label>
                            <input
                              type="date"
                              value={paymentDate}
                              onChange={(e) => setPaymentDate(e.target.value)}
                            />
                          </div>
                          <div>
                            <label>Amount Paid</label>
                            <input
                              type="number"
                              value={paymentAmount}
                              onChange={(e) => setPaymentAmount(e.target.value)}
                              placeholder="e.g., 900"
                            />
                          </div>
                          <div>
                            <label>Note</label>
                            <input
                              type="text"
                              value={paymentNote}
                              onChange={(e) => setPaymentNote(e.target.value)}
                              placeholder="optional note"
                            />
                          </div>
                          <div style={{ display: "flex", alignItems: "flex-end" }}>
                            <button className="green-button" onClick={handleRecordPayment}>
                              Save
                            </button>
                          </div>
                        </div>
                        <small style={{ display: "block", marginTop: 6, opacity: 0.75 }}>
                          Saving a payment writes to <code>subcontractorPay</code> and also attaches the payment to the time logs on that date (or creates a PAYMENT marker log if none).
                        </small>
                      </div>
                    </>
                  )}
                </div>

                <div
                  style={{
                    margin: "12px 0",
                    padding: 12,
                    border: "2px solid #d6a700",
                    borderRadius: 8,
                    backgroundColor: "#fffbea",
                  }}
                >
                  <h4 style={{ marginTop: 0 }}>TechAssign Time Verification</h4>
                  <p style={{ marginTop: 0 }}>
                    These dates are assigned to this user in Job Estimate Spreadsheet but do not have a Clock Time entry. Verify each date separately.
                  </p>

                  {checkingAssignedDays ? (
                    <p>Checking assigned dates...</p>
                  ) : missingAssignedDays.length === 0 ? (
                    <p><strong>No missing assigned dates found.</strong></p>
                  ) : (
                    missingAssignedDays.map((item) => (
                      <div
                        key={item.dateKey}
                        style={{
                          padding: 10,
                          marginBottom: 8,
                          backgroundColor: "#fff",
                          border: "1px solid #e2c45c",
                          borderRadius: 6,
                        }}
                      >
                        <div><strong>Date:</strong> {moment(item.dateKey).format("M/D/YYYY")}</div>
                        <div>
                          <strong>Assigned Jobs:</strong>{" "}
                          {item.jobs
                            .map((job) =>
                              [job.siteName, job.wo ? `WO ${job.wo}` : ""]
                                .filter(Boolean)
                                .join(" — ")
                            )
                            .join(", ")}
                        </div>
                        <div style={{ marginTop: 6 }}>
                          Add Clock In 8:00 AM, Clock Out 4:00 PM, Duration 8h 0m?
                        </div>
                        <div className="techassign-verification-actions">
                          <button
                            className="green-button"
                            disabled={addingAssignedDate === item.dateKey}
                            onClick={() => handleAddVerifiedAssignedDay(item)}
                          >
                            {addingAssignedDate === item.dateKey
                              ? "Adding..."
                              : "Yes, Add 8 Hours"}
                          </button>
                          <button
                            type="button"
                            className="techassign-cancel-button"
                            disabled={addingAssignedDate === item.dateKey}
                            onClick={() => handleDismissAssignedDay(item)}
                          >
                            No / Cancel
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>

                <ul>
  {(selectedUserLogs || []).map((log) => {
    const isOff = log.reason && /off/i.test(log.reason);
    const hasPayments = Array.isArray(log.payments) && log.payments.length > 0;

    // Detect logs 10h or more (whether or not they clocked out)
    let isLongShift = false;
    let longMinutes = 0;

    if (log.duration && log.duration > 0) {
      // finished shift: use stored duration
      longMinutes = log.duration;
      if (longMinutes >= 600) {
        isLongShift = true;
      }
    } else if (
      log.clockIn &&
      !log.clockOut &&
      !/off/i.test(log.reason || "")
    ) {
    
      // active shift: calculate from now
      const now = new Date();
      longMinutes = Math.round((now - log.clockIn) / 60000);
      if (longMinutes >= 600) {
        isLongShift = true;
      }
    }

    return (
      <li
        key={log.id}
        style={{
          color: isOff ? "red" : "inherit",
          backgroundColor: isLongShift ? "#fff8c4" : "transparent",
          border: isLongShift ? "1px solid #f0d96a" : "none",
          padding: isLongShift ? "6px" : "0px",
          borderRadius: isLongShift ? "4px" : "0px",
          marginBottom: "4px",
        }}
      >
        <strong>Clock In:</strong> {log.clockIn?.toLocaleString()} <br />
        {log.clockOut ? (
          <>
            <strong>Clock Out:</strong> {log.clockOut?.toLocaleString()} <br />
            <strong>Duration:</strong> {formatDuration(log.duration)} <br />
            {isLongShift && (
              <>
                <span>
                  ⚠️ <strong>Over 10 hours:</strong> Duration {formatDuration(longMinutes)}
                </span>
                <br />
              </>
            )}
          </>
        ) : (
          <>
            <strong>Clock Out:</strong> <em>Still clocked in</em> <br />
            {isLongShift && (
              <span>
                ⚠️ <strong>Over 10 hours:</strong> Elapsed {formatDuration(longMinutes)}
              </span>
            )}
          </>
        )}

        {log.reason && (
          <>
            <br />
            <strong>Notes:</strong> {log.reason} <br />
          </>
        )}

        {hasPayments && (
          <div style={{ marginTop: 4 }}>
            <strong>Payments on this log:</strong>
            <ul>
              {log.payments.map((p, idx) => (
                <li key={idx}>
                  {moment(p.datePaid?.toDate ? p.datePaid.toDate() : p.datePaid).format(
                    "YYYY-MM-DD"
                  )}{" "}
                  — ${Number(p.amountPaid || 0).toFixed(2)} {p.note ? `— ${p.note}` : ""}
                </li>
              ))}
            </ul>
          </div>
        )}

        <button onClick={() => handleEditLogClick(log)}>Edit</button>
        {deleteConfirmId === log.id ? (
          <div className="confirm-delete">
            <p>Are you sure?</p>
            <button
              onClick={() => {
                handleDeleteLog(log.id);
                setDeleteConfirmId(null);
              }}
            >
              Yes
            </button>
            <button onClick={() => setDeleteConfirmId(null)}>No</button>
          </div>
        ) : (
          <button onClick={() => setDeleteConfirmId(log.id)}>Delete</button>
        )}
      </li>
    );
  })}
</ul>


              </>
            ) : (
              <p>User information could not be loaded.</p>
            )}
          </div>
        </div>
      )}

      {editingLog && (
        <div className="edit-log-popup">
          <div className="edit-log-popup-content">
            <h3>Edit Log</h3>
            <label>Clock In</label>
            <input
              type="datetime-local"
              value={formatDateForInput(editingLog.clockIn)}
              onChange={(e) =>
                setEditingLog((prev) => ({
                  ...prev,
                  clockIn: new Date(e.target.value),
                  duration: prev.clockOut
                    ? Math.round((new Date(prev.clockOut) - new Date(e.target.value)) / 60000)
                    : null,
                }))
              }
            />
            <label>Clock Out</label>
            <input
              type="datetime-local"
              value={formatDateForInput(editingLog.clockOut)}
              onChange={(e) =>
                setEditingLog((prev) => ({
                  ...prev,
                  clockOut: new Date(e.target.value),
                  duration: e.target.value
                    ? Math.round((new Date(e.target.value) - new Date(prev.clockIn)) / 60000)
                    : null,
                }))
              }
            />
            <label>Duration (minutes)</label>
            <input type="number" value={editingLog.duration || ""} readOnly />
            <label>Notes</label>
            <textarea
              value={editingLog.reason || ""}
              onChange={(e) =>
                setEditingLog((prev) => ({
                  ...prev,
                  reason: e.target.value,
                }))
              }
            />
            <button onClick={handleEditLog}>Save</button>
            <button onClick={() => setEditingLog(null)}>Cancel</button>
          </div>
        </div>
      )}

      {showAddTimePopup && (
        <>
          <div className="modal-overlay" onClick={handleCancelNewTimeLog}></div>
          <div className="modal">
            <div className="add-time-popup-content">
              <h3>Add Time</h3>

              <label>Name</label>
              <select
                name="userId"
                value={newTimeLog.userId}
                onChange={handleAddTimeInputChange}
                required
              >
                <option value="">Select User</option>
                {usersList.map((u) => (
                  <option key={u.id} value={u.id}>{u.__display}</option>
                ))}
              </select>

              <label>Clock In</label>
              <input
                type="datetime-local"
                name="clockIn"
                value={newTimeLog.clockIn}
                onChange={handleAddTimeInputChange}
                required
              />

              <label>Clock Out</label>
              <input
                type="datetime-local"
                name="clockOut"
                value={newTimeLog.clockOut}
                onChange={handleAddTimeInputChange}
                required
              />

              <label>Duration (minutes)</label>
              <input type="number" name="duration" value={newTimeLog.duration} readOnly />

              <label>Notes</label>
              <textarea name="reason" value={newTimeLog.reason} onChange={handleAddTimeInputChange} />

              <div className="modal-buttons">
                <button onClick={handleSaveNewTimeLog}>Save</button>
                <button onClick={handleCancelNewTimeLog}>Cancel</button>
              </div>
            </div>
          </div>
        </>
      )}

      {showAddDayPopup && (
        <>
          <div className="modal-overlay" onClick={handleCancelNewDayLog}></div>
          <div className="modal">
            <div className="add-day-popup-content">
              <h3>Add Day</h3>

              <label>Name</label>
              <select
                name="userId"
                value={newDayLog.userId}
                onChange={(e) => handleAddDayInputChange(e)}
                required
              >
                <option value="">Select User</option>
                {usersList.map((u) => (
                  <option key={u.id} value={u.id}>{u.__display}</option>
                ))}
              </select>

              {newDayLog.days.map((day, index) => (
                <div key={index}>
                  <label>Day</label>
                  <input
                    type="date"
                    name="day"
                    value={day}
                    onChange={(e) => handleAddDayInputChange(e, index)}
                    required
                  />
                </div>
              ))}

              <button onClick={handleAddAnotherDay}>Add Another Day</button>

              <label>Notes</label>
              <textarea
                name="reason"
                value={newDayLog.reason}
                onChange={(e) => handleAddDayInputChange(e)}
              />

              <div className="modal-buttons">
                <button onClick={handleSaveNewDayLog}>Save</button>
                <button onClick={handleCancelNewDayLog}>Cancel</button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ClockTime;
